// app 1.5.63 — the home's idle logout, out of main.ts so it can be tested (test/cardtrip.test.ts).
//
// v0.20.10: "logged in" means the home view is mounted (there is no server session), so logging out is tearing the home
// down for the login screen; re-entry needs Face ID. Interval-based (every 15 s) so it survives a phone's timer
// throttling, and checked again whenever the page comes back (visibility, focus, a restore from the page cache).
// 1.5.63 (Alexander, 4 Nov: "if left as the last active screen in M app, it doesn't log out — that's a security hole") —
// a tap counts as activity only while the time hasn't run out: the first tap after IDLE_MS logs out instead of
// restarting the clock. Before, a home restored from Safari's page cache could be kept alive by a tap that came before
// the next 15-second check.

export const IDLE_MS = 3 * 60 * 1000;
const EVENTS = ["pointerdown", "keydown", "touchstart"] as const;

export interface IdleEnv {
  win: Pick<EventTarget, "addEventListener" | "removeEventListener"> & {
    setInterval(fn: () => void, ms: number): number;
    clearInterval(id: number): void;
  };
  doc: Pick<EventTarget, "addEventListener" | "removeEventListener"> & { hidden: boolean };
  now: () => number;
}

/** The idle watch: start() arms it (the home is up), stop() disarms it (the login screen), onIdle() logs out. */
export function idleWatch(onIdle: () => void, env: IdleEnv, ms = IDLE_MS) {
  let last = env.now();
  let timer: number | undefined;
  const armed = () => timer !== undefined;
  const check = (): boolean => {
    if (!armed()) return false;
    if (env.now() - last >= ms) { stop(); onIdle(); return true; }
    return false;
  };
  const onActivity = () => { if (!check()) last = env.now(); };
  const onShow = () => { if (!env.doc.hidden) check(); };
  function start() {
    last = env.now();
    if (armed()) return;
    EVENTS.forEach((e) => env.win.addEventListener(e, onActivity, { passive: true, capture: true }));
    env.doc.addEventListener("visibilitychange", onShow);
    env.win.addEventListener("pageshow", onShow);
    env.win.addEventListener("focus", onShow);
    timer = env.win.setInterval(check, 15000);
  }
  function stop() {
    if (timer !== undefined) { env.win.clearInterval(timer); timer = undefined; }
    EVENTS.forEach((e) => env.win.removeEventListener(e, onActivity, { capture: true }));
    env.doc.removeEventListener("visibilitychange", onShow);
    env.win.removeEventListener("pageshow", onShow);
    env.win.removeEventListener("focus", onShow);
  }
  return { start, stop, check };
}
